import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { Mail, ArrowLeft } from 'lucide-react';
import { BrandLogo } from '@/components/BrandLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/i18n/LanguageContext';
import { LanguageSelector } from '@/components/LanguageSelector';
import { TurnstileWidget } from '@/components/auth/TurnstileWidget';
import { isTurnstileBypassedEnv, verifyTurnstileToken } from '@/lib/turnstile';

const forgotPasswordSchema = z.object({
  email: z.string().email(),
});

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

export default function ForgotPassword() {
  const { t, language } = useLanguage();
  const { resetPassword } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileBypassed, setTurnstileBypassed] = useState(false);

  const handleTurnstileVerify = useCallback((token: string) => setTurnstileToken(token), []);
  const handleTurnstileExpire = useCallback(() => setTurnstileToken(null), []);
  const handleTurnstileTimeout = useCallback(() => setTurnstileBypassed(true), []);

  const skipTurnstile = isTurnstileBypassedEnv();

  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema as any),
    defaultValues: {
      email: '',
    },
  });

  const canSubmit = skipTurnstile || turnstileBypassed || !!turnstileToken;

  const onSubmit = async (values: ForgotPasswordFormValues) => {
    if (!canSubmit) {
      toast({ title: t.common.error, description: t.security.checkRequired, variant: 'destructive' });
      return;
    }
    setIsLoading(true);
    try {
      if (turnstileToken && !skipTurnstile) {
        const verification = await verifyTurnstileToken(turnstileToken);
        if (!verification.success) {
          toast({ title: t.common.error, description: verification.reason ?? t.security.verificationFailed, variant: 'destructive' });
          setTurnstileToken(null);
          return;
        }
      }
      const { error } = await resetPassword(values.email);
      if (error) {
        toast({ title: t.common.error, description: error.message, variant: 'destructive' });
      } else {
        setEmailSent(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-hero-gradient">
      <motion.div
        className="w-full max-w-md"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="absolute top-4 right-4">
          <LanguageSelector />
        </div>

        <Link to="/" className="mb-8 inline-block">
          <BrandLogo size="md" />
        </Link>

        {emailSent ? (
          <div className="text-center">
            <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Mail className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">
              {({ en: 'Check your email', es: 'Revisa tu correo', fr: 'Vérifiez votre e-mail', pt: 'Verifique seu e-mail', ar: 'تحقق من بريدك الإلكتروني', ja: 'メールを確認してください', ko: '이메일을 확인하세요', id: 'Periksa email Anda' } as Record<string, string>)[language] || 'Check your email'}
            </h1>
            <p className="text-muted-foreground mb-8">
              {({ en: "We've sent a password reset link to your email address.", es: 'Hemos enviado un enlace de restablecimiento a tu correo electrónico.', fr: 'Nous avons envoyé un lien de réinitialisation à votre adresse e-mail.', pt: 'Enviamos um link de redefinição para o seu e-mail.', ar: 'لقد أرسلنا رابط إعادة تعيين كلمة المرور إلى بريدك الإلكتروني.', ja: 'パスワードリセットリンクをメールアドレスに送信しました。', ko: '비밀번호 재설정 링크를 이메일로 보냈습니다.', id: 'Kami telah mengirim tautan reset kata sandi ke email Anda.' } as Record<string, string>)[language] || "We've sent a password reset link to your email address."}
            </p>
            <Link to="/login">
              <Button variant="outline" className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                {t.auth.backToLogin}
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-bold text-foreground mb-2">
              {t.auth.resetPassword}
            </h1>
            <p className="text-muted-foreground mb-8">
              {t.auth.resetPasswordSubtitle}
            </p>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t.auth.email}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="you@example.com"
                            className="pl-10"
                            {...field}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {!skipTurnstile && !turnstileBypassed && (
                  <TurnstileWidget onVerify={handleTurnstileVerify} onExpire={handleTurnstileExpire} onLoadTimeout={handleTurnstileTimeout} />
                )}

                <Button type="submit" className="w-full" disabled={isLoading || !canSubmit}>
                  {isLoading ? t.common.loading : t.auth.sendResetLink}
                </Button>
              </form>
            </Form>

            <p className="mt-8 text-center">
              <Link to="/login" className="text-sm text-secondary hover:underline inline-flex items-center gap-1">
                <ArrowLeft className="h-4 w-4" />
                {t.auth.backToLogin}
              </Link>
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
}
